import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type {
	APIData,
	DateRangeInterface,
	DSSetup,
	Recipient,
	RecordData,
	Service,
	Slack,
} from 'webapp-types';
import type { AlertEventCondition, DeliveryType, EventDeliveryTime } from 'webapp/enums/enums';
import { DateRangeSelectionId, GranulationTypeId, ModelName, TimeUnit } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';
import { getFormattedTime } from 'webapp/utils/number';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import DateRange from 'webapp/models-custom/date-range';
import MetricLimitations from 'webapp/models-custom/metric-limitations';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type {
	OnChangeProp as MetricSetupOnChangeProp,
	OnChangePropValue as MetricSetupOnChangePropValue,
} from 'webapp/models-custom/metric-setup-base';
import type Source from 'webapp/models-custom/source';
import type AlertRecord from 'webapp/models/alert-setting';

import { Severity } from 'webapp/services/logger';

export interface AlertRangeOption {
	value: TimeUnit;
	label: string;
	dateRanges: DateRange[];
	label3?: string;
	disabled?: boolean;
}

export interface AlertColor {
	text: string;
	bg: string;
}

export default class Alert extends BufferedModel<AlertRecord, RecordData.AlertSetting.Serialized> {
	@service('constants') declare constantsService: Service.Constants;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/alerts') declare mmAlertsService: Service.ModelManager.Alerts;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get parentId(): number | null {
		return this.getBuffered('parentId');
	}

	set parentId(value: number | null) {
		this.setBuffered('parentId', value);
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get metricId(): number | null {
		return this.getBuffered('metricId');
	}

	set metricId(value: number | null) {
		this.setBuffered('metricId', value);
	}

	get blockId() {
		return this.model.blockId;
	}

	get metricSetupKey(): string {
		return this.getBuffered('metricSetupKey');
	}

	set metricSetupKey(value: string) {
		this.setBuffered('metricSetupKey', value);
	}

	get metricSetupSpaceAccessId(): number | null {
		return this.getBuffered('metricSetupSpaceAccessId');
	}

	set metricSetupSpaceAccessId(value: number | null) {
		this.setBuffered('metricSetupSpaceAccessId', value);
	}

	get metricSetupDimensions(): string[] {
		return this.getBuffered('metricSetupDimensions');
	}

	set metricSetupDimensions(value: string[]) {
		this.setBuffered('metricSetupDimensions', value);
	}

	get metricSetupFilters(): DSSetup.MetricSetupFiltersData | null {
		return this.getBuffered('metricSetupFilters');
	}

	set metricSetupFilters(value: DSSetup.MetricSetupFiltersData | null) {
		this.setBuffered('metricSetupFilters', value);
	}

	get dateRange(): DateRangeInterface.FormulaInterface {
		return this.getBuffered('dateRange');
	}

	set dateRange(value: DateRangeInterface.FormulaInterface) {
		this.setBuffered('dateRange', value);
	}

	get dataSourceId(): number | null {
		return this.getBuffered('dataSourceId');
	}

	set dataSourceId(value: number | null) {
		this.setBuffered('dataSourceId', value);
	}

	get inactive(): boolean {
		return this.getBuffered('inactive');
	}

	set inactive(value: boolean) {
		this.setBuffered('inactive', value);
	}

	get condition(): AlertEventCondition {
		return this.getBuffered('condition');
	}

	set condition(value: AlertEventCondition) {
		this.setBuffered('condition', value);
	}

	get qualifier(): number | null {
		return this.getBuffered('qualifier');
	}

	set qualifier(value: number | null) {
		this.setBuffered('qualifier', value);
	}

	get deliveryTime(): EventDeliveryTime {
		return this.getBuffered('deliveryTime');
	}

	set deliveryTime(value: EventDeliveryTime) {
		this.setBuffered('deliveryTime', value);
	}

	get deliveryType(): DeliveryType[] {
		return this.getBuffered('deliveryType');
	}

	set deliveryType(value: DeliveryType[]) {
		this.setBuffered('deliveryType', value);
	}

	get slackChannels(): Slack.Channels {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channels) {
		this.setBuffered('slackChannels', value);
	}

	get template(): string {
		return this.getBuffered('template');
	}

	set template(value: string) {
		this.setBuffered('template', value);
	}

	get userIds(): number[] {
		return this.getBuffered('userIds');
	}

	set userIds(value: number[]) {
		this.setBuffered('userIds', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdById(): number {
		return this.model.createdById;
	}

	get isActive() {
		return !this.inactive;
	}

	set isActive(value) {
		this.inactive = !value;
		this.save().catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	private handleMetricSetupUpdate(
		prop: MetricSetupOnChangeProp,
		value: MetricSetupOnChangePropValue,
	) {
		switch (prop) {
			case 'key':
				this.metricSetupKey = value as string;
				break;
			case 'spaceAccessId':
				this.metricSetupSpaceAccessId = value as number | null;
				break;
			case 'dimensions':
				this.metricSetupDimensions = value as string[];
				break;
			case 'filters':
				this.metricSetupFilters = value as DSSetup.MetricSetupFiltersData;
				break;
		}
	}

	@cached
	get metricSetup(): MetricSetup {
		return new MetricSetup(
			{
				key: this.metricSetupKey,
				spaceAccessId: this.metricSetupSpaceAccessId,
				dimensions: this.metricSetupDimensions,
				filters: this.metricSetupFilters,
			},
			this.handleMetricSetupUpdate.bind(this),
		);
	}

	@cached
	get sourceObj(): Source | null {
		return this.metricSetup?.metric?.source || null;
	}

	get isMine() {
		return this.createdById === this.currentUserService.numericId;
	}

	get createdBy() {
		let user;
		if (this.createdById) {
			user = this.mmUsersService.findRecord(String(this.createdById));
			if (!user) {
				this.loggerService.logError(
					'Missing createdBy for alert',
					{
						alertId: this.id,
						userId: this.createdById,
					},
					Severity.WARNING,
				);
			}
		}
		return user;
	}

	@cached
	get createdText() {
		let text = getFormattedTime(this.created, this.currentUserService.model?.timezone);
		if (this.createdBy) {
			text += `<br /> by ${this.createdBy.name}`;
		}
		return text;
	}

	@cached
	get defaultTemplate() {
		return this.mmAlertsService.getDefaultTemplateForCondition(this.condition);
	}

	@cached
	get deliveryTypeText() {
		const deliveryTypeLabels: string[] = [];
		this.deliveryType.forEach((type) => {
			const obj = this.constantsService.eventDeliveryTypes.find(
				(item) => item.value === type,
			);
			if (obj) {
				deliveryTypeLabels.push(obj.label);
			}
		});
		return deliveryTypeLabels.join(', ');
	}

	get deliveryTypeCount() {
		return this.deliveryType.length;
	}

	get titleForSlackList() {
		return this.name;
	}

	get slackChannelCountForSlackList() {
		return this.slackChannels.length;
	}

	// Used in notification slack section where is a possibility for sorting
	get deliveryTimeLevel() {
		return this.deliveryTime === 1 ? -2 : -1;
	}

	@cached
	get deliveryTimeText() {
		const obj = this.constantsService.eventDeliveryTimes.find(
			(item) => item.value === this.deliveryTime,
		);
		return obj ? obj.label : '';
	}

	@cached
	get hasPercentValue() {
		const condition = this.constantsService.eventConditions.find(
			(item) => item.value === this.condition,
		);
		return !!condition && !!condition.isPercent;
	}

	get allRangeOptions() {
		return Alert.rangeOptions;
	}

	@tracked _rangeOptions: AlertRangeOption[] | null = null;

	get rangeOptions() {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		this.limitationsCM; // trigger autotracking dependency
		return this._rangeOptions || this.allRangeOptions;
	}

	set rangeOptions(options) {
		this._rangeOptions = options;
	}

	private _metricLimitations: MetricLimitations | null = null;

	get limitationsCM() {
		if (!this._metricLimitations) {
			const dateRange = DateRange.createFromFormula(this.dateRange);
			dateRange.updateFromPredefinedGranulationId(GranulationTypeId.TOTAL);

			const limitations = new MetricLimitations({
				metrics: this.metricSetup?.metricData ? [this.metricSetup.metricData] : [],
				isNumber: true,
				dateRange,
			});

			this._metricLimitations = limitations;
		} else if (
			this.metricSetup?.key &&
			this._metricLimitations.metrics[0]?.key !== this.metricSetup.key
		) {
			next(this, () => {
				this._metricLimitations?.changeMetrics([this.metricSetup.metricData!]);
			});
			next(this, this.updateRangesFromLimitations);
		}

		return this._metricLimitations;
	}

	@cached
	get selectedRangeId() {
		const selected = this.rangeOptions.find((option) =>
			option.dateRanges.some((dr) => dr.isRangeEqual(this.dateRange)),
		);
		let selectedRangeId;

		if (selected) {
			selectedRangeId = selected.value;
		} else if (this.rangeOptions.length) {
			// select first one
			const selectedRangeId = this.rangeOptions.find((item) => !item.disabled)?.value;
			if (selectedRangeId) {
				this.updateDateRangeFromSelectedRangeId(selectedRangeId);
			}
		}

		return selectedRangeId;
	}

	set selectedRangeId(value) {
		if (value) {
			next(this, this.updateDateRangeFromSelectedRangeId, value);
		}
	}

	updateDateRangeFromSelectedRangeId(timeUnit: TimeUnit) {
		const selected = this.rangeOptions.find((item) => item.value === timeUnit);
		next(() => {
			if (selected) {
				const isSettingDefault = this.isNew && !Object.keys(this.dateRange).length;
				this.dateRange = selected.dateRanges[0]!.formula;
				if (isSettingDefault) {
					this.applyBufferedChanges(['dateRange']);
				}
			}
		});
	}

	@cached
	get isUserSubscriber() {
		return !this.userIds.length || this.userIds.includes(this.currentUserService.numericId!);
	}

	get usersCount() {
		return this.userIds.length;
	}

	@cached
	get mappedRecipients(): Recipient[] {
		return this.mmUsersService.getMappedRecipients([], this.userIds, this.slackChannels);
	}

	get isScheduledForMe() {
		return this.userIds.includes(this.currentUserService.numericId!);
	}

	get trackingInfo() {
		const eventData: APIData.Tracking.Data = {},
			datasource = this.sourceObj?.sourceType?.key,
			metricName = this.metricSetup?.metric?.name;

		if (datasource) {
			eventData.datasource = datasource;
		}

		if (metricName) {
			eventData.metrics = [metricName];
		}

		return eventData;
	}

	updateRangesFromLimitations() {
		const allRangeOptions = this.allRangeOptions,
			limitations = this.limitationsCM;

		const cleanupCircularRefsFn = (options: AlertRangeOption[]) => {
			return options.map((opt) => ({
				...opt,
				dateRanges: opt.dateRanges.map((dr) => dr.formula),
			}));
		};

		void limitations.limitationsLoaded?.then(() => {
			const options: AlertRangeOption[] = [];

			allRangeOptions.forEach((option) => {
				const supportedDateRanges: DateRange[] = [],
					rangesLimitedByPricing: DateRange[] = [];
				let disabledTooltipText = '';

				option.dateRanges.forEach((dateRange) => {
					if (limitations.isDateRangeSupported(dateRange)) {
						supportedDateRanges.push(dateRange);
					} else {
						const limitation = limitations.getLimitationsForRange(
							dateRange.rangeTypeId,
						);
						if (limitation.isPaidLimitation) {
							disabledTooltipText = limitation.infoText;
							rangesLimitedByPricing.push(dateRange);
						}
					}
				});

				if (supportedDateRanges.length) {
					options.push({
						value: option.value,
						label: option.label,
						dateRanges: supportedDateRanges,
					});
				} else if (rangesLimitedByPricing.length) {
					options.push({
						value: option.value,
						label: option.label,
						dateRanges: rangesLimitedByPricing,
						label3: `<ui-icon name="Lock" size="sm" class="green" title="${disabledTooltipText}"></ui-icon>`,
						disabled: true,
					});
				}
			});

			if (!equal(cleanupCircularRefsFn(this.rangeOptions), cleanupCircularRefsFn(options))) {
				this.rangeOptions = options;
			}
		});
	}

	getDataForClone() {
		const data = this.toJSON();
		delete data.id;
		delete data.created;
		delete data.createdById;

		data.parentId = this.numericId;

		return data;
	}

	createAnotherModel() {
		return new Alert({ model: this.model });
	}

	static colors: AlertColor[] = [
		{ text: '#8e929b', bg: '#e6e6e9' },
		{ text: '#00b3ad', bg: '#d8f0ef' },
		{ text: '#f8931f', bg: '#fff7c7' },
		{ text: '#d91c5c', bg: '#fce0dd' },
	];

	static _normalRangeOptions: AlertRangeOption[] | null = null;

	static get rangeOptions(): AlertRangeOption[] {
		if (!this._normalRangeOptions) {
			const options = [];

			let dateRange;

			const lastXDateRange = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.LAST_X,
				GranulationTypeId.TOTAL,
			);
			lastXDateRange.rangeIncludeCurrent = false;

			// Last 24 hours
			dateRange = lastXDateRange.createCopy();
			dateRange.rangeTimeUnit = TimeUnit.HOUR;
			dateRange.rangeMultiplier = 24;

			options.push({ value: 101, label: dateRange.rangeTitleUX, dateRanges: [dateRange] });

			// Last 7 days
			dateRange = lastXDateRange.createCopy();
			dateRange.rangeTimeUnit = TimeUnit.DAY;
			dateRange.rangeMultiplier = 7;

			options.push({ value: 102, label: dateRange.rangeTitleUX, dateRanges: [dateRange] });

			// Last 30 days
			dateRange = lastXDateRange.createCopy();
			dateRange.rangeTimeUnit = TimeUnit.DAY;
			dateRange.rangeMultiplier = 30;

			options.push({ value: 103, label: dateRange.rangeTitleUX, dateRanges: [dateRange] });

			// Last 90 days
			dateRange = lastXDateRange.createCopy();
			dateRange.rangeTimeUnit = TimeUnit.DAY;
			dateRange.rangeMultiplier = 90;

			options.push({ value: 104, label: dateRange.rangeTitleUX, dateRanges: [dateRange] });

			this._normalRangeOptions = options;
		}

		return this._normalRangeOptions;
	}

	static override modelName = ModelName.ALERT_SETTING; // store record type
}
